import { BOARD_COLUMN_LABEL, CLOSED_RESULT_LABEL, type BoardCardDTO, type BoardScope, type ClosedResult, type TableroColumn } from "@bgreen/shared";
import { Hand, Lock } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { cn } from "../../lib/cn";
import { rememberOpened, span, STATE_SHORT, useBoardPages } from "../../lib/board";
import { Button } from "../ui/button";
import { Chip, Skeleton } from "../ui/primitives";
import { BoardCard, PriorityMark, repoShort, RESULT_TONE, timesTitle } from "./BoardCard";

interface Props {
  scope: BoardScope;
  column: TableroColumn | "";
  result: ClosedResult | "";
  filters: URLSearchParams;
  selectedId: string | null;
  mobile: boolean;
  emptyText: string;
}

const ago = (iso: string | null) => { const s = span(iso); return s ? `hace ${s}` : "—"; };
/** «Fase» de una misión terminada: de dónde se consulta (Tablero reciente o Cerradas), no una fase de trabajo. */
const phaseCell = (c: BoardCardDTO) => (c.result ? (c.recentlyCompleted ? "Completada reciente" : BOARD_COLUMN_LABEL.closed) : BOARD_COLUMN_LABEL[c.column]);

function FooterCount({ shown, total, hasNext, loading, onMore }: { shown: number; total: number; hasNext: boolean; loading: boolean; onMore: () => void }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 py-3 text-[13px] text-mu">
      <span>Mostrando {shown} de {total}</span>
      {hasNext && <Button variant="secondary" size="sm" loading={loading} onClick={onMore}>Mostrar más · {Math.max(0, total - shown)} restantes</Button>}
    </div>
  );
}

function StateCell({ c }: { c: BoardCardDTO }) {
  if (c.result) return <Chip tone={RESULT_TONE[c.result]} className="h-6 px-2 text-[12px]">{CLOSED_RESULT_LABEL[c.result]}</Chip>;
  const paused = c.indicator === "blocked" || c.indicator === "error" || c.indicator === "awaiting_budget";
  return <span className={cn("text-[13px]", paused && (c.indicator === "error" ? "font-medium text-er" : "font-medium text-wn"))}>{STATE_SHORT[c.status]}</span>;
}

/** Lista paginada desde el servidor; en móvil, como tarjetas. */
export function ListView({ scope, column, result, filters, selectedId, mobile, emptyText }: Props) {
  const nav = useNavigate();
  const q = useBoardPages(filters, { scope, column, result, limit: 30 });
  const items = q.data?.pages.flatMap((p) => p.items) ?? [];
  const total = q.data?.pages[0]?.total ?? 0;
  const footer = <FooterCount shown={items.length} total={total} hasNext={!!q.hasNextPage} loading={q.isFetchingNextPage} onMore={() => void q.fetchNextPage()} />;

  if (q.isLoading) return <div className="flex flex-col gap-2">{Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-14" />)}</div>;
  if (q.isError) return <div className="py-6 text-[14px] text-er">No se pudo cargar la lista. Se reintenta solo.</div>;
  if (items.length === 0) return <div className="rounded-card border border-ln px-4 py-8 text-center text-[14px] text-mu">{emptyText}</div>;

  if (mobile) {
    return (
      <div>
        <div className="grid grid-cols-1 gap-3">
          {items.map((m) => <BoardCard key={m.id} c={m} selected={selectedId === m.id} />)}
        </div>
        {footer}
      </div>
    );
  }

  const open = (id: string) => { rememberOpened(id); void nav(`/missions/${id}`); };
  const th = "sticky top-0 z-10 whitespace-nowrap border-b border-ln bg-bg px-3 py-2.5 text-left font-mono text-[11px] font-bold uppercase tracking-[0.12em] text-mu";
  return (
    <div>
      <table className="w-full border-separate border-spacing-0 text-[13.5px]">
        <thead>
          <tr>
            <th className={th}>ID</th><th className={th}>Título</th><th className={th}>Repositorio</th><th className={th}>Prioridad</th><th className={th}>Fase</th><th className={th}>Estado</th>
            <th className={th}>Intervención / motivo</th><th className={th} title="Tiempo en la fase actual (o cierre)">En fase</th><th className={th}>Última actividad</th><th className={cn(th, "max-2xl:hidden")}>Creada</th>
          </tr>
        </thead>
        <tbody>
          {items.map((c) => (
            <tr key={c.id} data-mission-id={c.id} onClick={() => open(c.id)} className={cn("cursor-pointer hover:bg-hv", selectedId === c.id && "bg-hv outline outline-2 -outline-offset-2 outline-ac")}>
              <td className="whitespace-nowrap border-b border-ln px-3 py-2.5 align-top font-mono text-[12.5px]"><a href={`/missions/${c.id}`} onClick={(e) => { e.preventDefault(); e.stopPropagation(); open(c.id); }} className="text-tx no-underline hover:underline">{c.key}</a></td>
              <td className="max-w-[360px] border-b border-ln px-3 py-2.5 align-top"><div className="line-clamp-2 font-medium" title={c.title}>{c.title}</div></td>
              <td className="border-b border-ln px-3 py-2.5 align-top"><div className="font-mono text-[12.5px]">{repoShort(c.repositoryName)}</div><div className="text-[12px] text-mu">{c.projectName}</div></td>
              <td className="whitespace-nowrap border-b border-ln px-3 py-2.5 align-top"><PriorityMark p={c.priority} /></td>
              <td className="whitespace-nowrap border-b border-ln px-3 py-2.5 align-top text-mu">{phaseCell(c)}</td>
              <td className="whitespace-nowrap border-b border-ln px-3 py-2.5 align-top"><StateCell c={c} /></td>
              <td className="max-w-[300px] border-b border-ln px-3 py-2.5 align-top text-[12.5px]">
                {c.reason && <div className="line-clamp-2 text-mu" title={c.reason}>{c.reason}</div>}
                {c.intervention && !c.result && (
                  <div className={cn("mt-0.5 flex items-start gap-1.5", c.intervention.canAct ? "font-medium" : "text-mu")} title={c.intervention.canAct ? "Puedes hacerlo con tu rol" : "Tu rol no permite esta acción"}>
                    {c.intervention.canAct ? <Hand className="mt-[2px] h-3.5 w-3.5 shrink-0 text-ac" aria-hidden /> : <Lock className="mt-[2px] h-3.5 w-3.5 shrink-0" aria-hidden />}{c.intervention.label}
                  </div>
                )}
              </td>
              <td className="whitespace-nowrap border-b border-ln px-3 py-2.5 align-top font-mono text-[12.5px]" title={timesTitle(c)}>{c.result ? (c.closedAt ? ago(c.closedAt) : "sin fecha") : (span(c.phaseSince) ?? "—")}</td>
              <td className="whitespace-nowrap border-b border-ln px-3 py-2.5 align-top font-mono text-[12.5px] text-mu">{ago(c.lastActivityAt)}</td>
              <td className="whitespace-nowrap border-b border-ln px-3 py-2.5 align-top font-mono text-[12.5px] text-mu max-2xl:hidden">{ago(c.createdAt)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {footer}
    </div>
  );
}
